/**
 * ReflectionGroupings — canonical Yadid "four groupings" per movement and
 * per continuous dynamic. Used by the progressive-prompts strip inside the
 * Reflection Canvas and by the Guided Reflection walkthrough.
 *
 * Feb 2026 refinement — the prompt content per movement/dynamic was
 * significantly upgraded during the Embrace Consolidation cycle by
 * merging in the canonical strategic questions that previously lived in
 * the retired MovementWorkspace / LensWorkspace surfaces:
 *   · Movements  — canonical questions from `backend/routers/method.py`
 *                  DEFAULT_PHASES[key].questions (marked ◆ in comments).
 *   · Dynamics   — CONTINUOUS_DYNAMICS[key].groupings from
 *                  `frontend/src/components/method/continuousDynamics.js`
 *                  (single source of truth via the adapter below).
 *
 * Groupings are the SAME four for every movement and every dynamic, in
 * the same fixed order: Present Position → System Dynamics → Strategic
 * Leverage → Next Move. Underlying keys stay stable (current_reality /
 * system_dynamics / strategic_need / next_move) so any older references
 * continue to resolve.
 */

import { CONTINUOUS_DYNAMICS } from "@/components/method/continuousDynamics";

// Editorial labels (Feb 2026). Underlying keys stay stable so back-compat
// with any older references, seeded content, or per-node prompt banks
// below is preserved.
export const GROUPING_ORDER = [
  { key: "current_reality", label: "Present Position",   roman: "I"   },
  { key: "system_dynamics", label: "System Dynamics",    roman: "II"  },
  { key: "strategic_need",  label: "Strategic Leverage", roman: "III" },
  { key: "next_move",       label: "Next Move",          roman: "IV"  },
];

// Fallback prompts — used when a node hasn't defined its own set.
const FALLBACK = {
  current_reality: [
    "What is actually true in this room, right now?",
    "Which relationships are carrying the weight of this work?",
  ],
  system_dynamics: [
    "Whose voice or lived experience is missing?",
    "Where are power, risk, or responsibility unevenly distributed?",
  ],
  strategic_need: [
    "What does this engagement most need us to be right about?",
  ],
  next_move: [
    "What is the single most important next move — and who owns it?",
  ],
};

// ─────────────────────────────────────────────────────────────────────
// MOVEMENT_GROUPINGS · Feb 2026 refinement
// Each movement's four groupings blend canonical Yadid questions (◆) —
// recovered from `DEFAULT_PHASES[key].questions` in the backend — with
// the strongest additional prompts previously carried in the canvas.
// ≤4 prompts per grouping; no duplication across groupings.
// ─────────────────────────────────────────────────────────────────────
export const MOVEMENT_GROUPINGS = {
  listen: {
    current_reality: [
      // Feb 2026 · Listen reframed to lead with proactive understanding
      // of the client's heart, aspirations, and meaning. Gap-finding
      // (missing voices, untested assumptions, the unspoken) moves to
      // Next Move below as the disciplined secondary pass.
      "What appears to matter most deeply to the client — including what may not yet have been fully articulated?",
      "What do they most desire this engagement to accomplish?",
      "What are they hoping will become possible for their organisation, their people, or themselves?",
      "What matters most to them here — the meaning behind the mandate?",
    ],
    system_dynamics: [
      "How do they themselves understand the challenge or opportunity in front of them?",
      "What values, relationships, experiences, or convictions are shaping their perspective?",
      "What are they concerned about losing, damaging, or getting wrong?",
      "Where do their public words and their private hopes diverge?",
    ],
    strategic_need: [
      "What must we understand truly, not approximately, before we allow ourselves to interpret?",
      "What language, story, or metaphor do they use to describe the situation to themselves?",
      "Where would the client feel genuinely heard versus merely processed?",
      "What is the smallest signal that would tell us we are not yet listening well enough?",
    ],
    next_move: [
      "Whose voice, experience, or lived reality have we not yet let in?",
      "What assumptions have we carried in that we have not tested with them?",
      "What has remained unspoken that our next conversation must invite into the room?",
    ],
  },
  reveal: {
    current_reality: [
      "Which relationships currently carry the engagement — and whose is at risk?",
      "Where is trust strong, fragile, borrowed, or absent?",
      "What story is this work telling internally — is it the one we want?",
    ],
    system_dynamics: [
      "What have we been treating as a constraint that is actually a preference?", // ◆ canonical
      "Whose voice or lived experience is missing?",
      "Where are power, risk, or responsibility unevenly distributed?",
    ],
    strategic_need: [
      "What is the true problem underneath the symptom?",                          // ◆ canonical
      "What single sentence, if agreed, would change every subsequent choice?",    // ◆ canonical
      "What framing would the client themselves recognize as accurate?",
    ],
    next_move: [
      "Which conversation must happen — and in what order — before we act?",
      "What is the smallest, truest sentence we could put in front of the sponsor this week?",
    ],
  },
  reimagine: {
    current_reality: [
      "Which possibilities have we quietly ruled out — and why?",
      "What is our own attachment doing to the shape of the options?",
    ],
    system_dynamics: [
      "What would change if we removed the assumption we're most confident in?",
      "What are we currently protecting that we should relinquish?",                // ◆ canonical
      "Which stakeholder's imagination are we not making room for?",
    ],
    strategic_need: [
      "What would we do if we had twice the boldness — or half the budget?",        // ◆ canonical
      // Below replaces the original CEO/enterprise phrasing per Feb 2026 client-
      // scope refinement — language now works across corporate, philanthropic,
      // and mission-driven clients Yadid serves.
      "Which option feels safest for leadership, and which best serves the organisation's mission and long-term interests?",
      "What are three genuinely different futures this engagement could produce?",
    ],
    next_move: [
      "Which option deserves a small, cheap test before we commit further?",
      "What is the boldest option that is still credible to the client?",
    ],
  },
  guide: {
    current_reality: [
      "What is the client ready to walk with — and what will they resist?",
      "Where do we need to be in the room, and where can we step back?",
    ],
    system_dynamics: [
      "What is the drift we're watching for — and who is watching it with us?",     // ◆ canonical
      "Where is the strategy meeting real friction, and what does that friction teach us?", // ◆ canonical
      "What supporting conditions must hold for this to land well?",
    ],
    strategic_need: [
      "What does the client need from us in the next 30 days that only we can provide?",
      "Who else needs to guide alongside us for this to work?",
    ],
    next_move: [
      "What would the next principal-to-principal moment be, if we scheduled it today?", // ◆ canonical
      "What is the smallest visible sign that we're guiding well?",
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────
// DYNAMIC_GROUPINGS · Adapter to CONTINUOUS_DYNAMICS.
// Feb 2026 · Single source of truth. The canonical Yadid teaching
// content for the two continuous dynamics lives in
// `components/method/continuousDynamics.js` as `groupings: [{ key,
// label, prompts }, …]`. We flatten that into the same shape the
// Progressive Prompts strip expects: { [groupingKey]: [prompt, …] }.
// ─────────────────────────────────────────────────────────────────────
function flattenDynamicGroupings(dynamicKey) {
  const dyn = CONTINUOUS_DYNAMICS[dynamicKey];
  if (!dyn?.groupings) return null;
  const out = {};
  for (const g of dyn.groupings) {
    out[g.key] = g.prompts || [];
  }
  return out;
}

export const DYNAMIC_GROUPINGS = {
  connections: flattenDynamicGroupings("connections") || {},
  leadership:  flattenDynamicGroupings("leadership")  || {},
};

/**
 * Resolve the prompts array for a given node (movement or dynamic) and
 * grouping key. Falls back to generic prompts when the node hasn't
 * defined its own set.
 */
export function promptsFor(nodeType, nodeKey, groupingKey) {
  const bank = nodeType === "movement" ? MOVEMENT_GROUPINGS : DYNAMIC_GROUPINGS;
  const perNode = bank[nodeKey] || {};
  return perNode[groupingKey] || FALLBACK[groupingKey] || [];
}
